import React, { useEffect, useMemo, useRef, useState } from 'react';

export type OnboardingStep = {
  id: string;
  title: string;
  body: string;
  target?: string;
  view?: string;
};

type OnboardingTourProps = {
  userId: string | null;
  activeView: string;
  onViewChange: (view: string) => void;
  steps: OnboardingStep[];
};

type Rect = { left: number; top: number; width: number; height: number };

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

const getRect = (el: Element): Rect => {
  const r = el.getBoundingClientRect();
  return { left: r.left, top: r.top, width: r.width, height: r.height };
};

const getTooltipPosition = (rect: Rect | null) => {
  const margin = 16;
  const width = 360;
  const minWidth = 280;
  const tooltipWidth = window.innerWidth < width + margin * 2 ? minWidth : width;

  if (!rect) {
    return {
      left: clamp((window.innerWidth - tooltipWidth) / 2, margin, window.innerWidth - tooltipWidth - margin),
      top: clamp(window.innerHeight * 0.18, margin, window.innerHeight - margin),
      width: tooltipWidth,
    };
  }

  const preferBelow = rect.top + rect.height + 16 + 220 <= window.innerHeight;
  const top = preferBelow ? rect.top + rect.height + 16 : rect.top - 16 - 220;
  const left = clamp(rect.left + rect.width / 2 - tooltipWidth / 2, margin, window.innerWidth - tooltipWidth - margin);

  return { left, top: clamp(top, margin, window.innerHeight - 240), width: tooltipWidth };
};

const storageKeyForUser = (userId: string) => `soartistic_ads_onboarding_done_${userId}`;

const OnboardingTour: React.FC<OnboardingTourProps> = ({ userId, activeView, onViewChange, steps }) => {
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const [missingTarget, setMissingTarget] = useState(false);
  const rafRef = useRef<number | null>(null);

  const canRun = useMemo(() => {
    if (!userId) return false;
    try {
      return localStorage.getItem(storageKeyForUser(userId)) !== '1';
    } catch {
      return false;
    }
  }, [userId]);

  useEffect(() => {
    if (!canRun) return;
    setOpen(true);
    setStepIndex(0);
  }, [canRun]);

  const close = (markDone: boolean) => {
    if (markDone && userId) {
      try {
        localStorage.setItem(storageKeyForUser(userId), '1');
      } catch {
        // ignore
      }
    }
    setOpen(false);
    setRect(null);
    setMissingTarget(false);
  };

  const currentStep = steps[stepIndex] || null;

  const measure = () => {
    if (!currentStep?.target) {
      setRect(null);
      setMissingTarget(false);
      return;
    }

    const candidates = Array.from(document.querySelectorAll(currentStep.target));
    const el = candidates.find((c) => {
      const r = c.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    });

    if (!el) {
      setRect(null);
      setMissingTarget(true);
      return;
    }

    try {
      (el as HTMLElement).scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
    } catch {
      // ignore
    }

    setRect(getRect(el));
    setMissingTarget(false);
  };

  useEffect(() => {
    if (!open) return;
    if (!currentStep) return;

    const desiredView = currentStep.view;
    if (desiredView && desiredView !== activeView) {
      onViewChange(desiredView);
    }

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      measure();
    });

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [open, stepIndex, currentStep?.id, activeView, onViewChange]);

  useEffect(() => {
    if (!open) return;

    const onResize = () => measure();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close(true);
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, stepIndex, currentStep?.id]);

  const prev = () => {
    setStepIndex((i) => Math.max(0, i - 1));
  };

  const next = () => {
    setStepIndex((i) => {
      const nextIndex = i + 1;
      if (nextIndex >= steps.length) {
        close(true);
        return i;
      }
      return nextIndex;
    });
  };

  if (!open || !currentStep) return null;

  const padding = rect ? 10 : 0;
  const highlightStyle: React.CSSProperties | null = rect
    ? {
        left: Math.max(0, rect.left - padding),
        top: Math.max(0, rect.top - padding),
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
        boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
        borderRadius: 16,
      }
    : null;

  const tooltip = getTooltipPosition(rect);
  const isLast = stepIndex === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0 bg-black/55" onClick={() => close(true)} />

      {highlightStyle && (
        <div
          className="fixed bg-transparent ring-2 ring-white/80"
          style={highlightStyle}
        />
      )}

      <div
        className="fixed"
        style={{ left: tooltip.left, top: tooltip.top, width: tooltip.width }}
      >
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs text-gray-500 font-medium">
                  Paso {stepIndex + 1} de {steps.length}
                </div>
                <div className="text-lg font-bold text-gray-900 leading-tight">
                  {currentStep.title}
                </div>
              </div>
              <button
                type="button"
                className="shrink-0 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={() => close(true)}
              >
                Saltar
              </button>
            </div>
          </div>

          <div className="px-5 py-4">
            <div className="text-gray-700 text-sm leading-relaxed">
              {currentStep.body}
            </div>
            {missingTarget && (
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                No encuentro el elemento de este paso en esta pantalla. Puedes seguir igualmente.
              </div>
            )}
          </div>

          <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={prev}
              disabled={stepIndex === 0}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Anterior
            </button>

            <div className="flex items-center gap-1.5">
              {steps.map((s, idx) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStepIndex(idx)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${
                    idx === stepIndex ? 'bg-blue-600' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Ir al paso ${idx + 1}`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={next}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              {isLast ? 'Finalizar' : 'Siguiente'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
